import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import QuoteForm from '@/components/QuoteForm';
import {
  Phone, Shield, Leaf, MapPin, Star, Clock, Users, DollarSign, ThumbsUp,
  Sofa, Monitor, BedDouble, Home, Warehouse, Car, Building2, Trash2,
  TreePine, Briefcase, Hammer, Fence, Bath, Truck, ChevronRight
} from 'lucide-react';

// Brand Assets
const HERO_BG = "https://customer-assets.emergentagent.com/job_clearout-pro/artifacts/zbsy72cb_JunkRemovalMD-Social%20Media%20Post.jpg";
const BEFORE_IMG = "https://customer-assets.emergentagent.com/job_clearout-pro/artifacts/462q2g8x_polish_20211220_193657240-A0x138EkoaikyxnQ.avif";
const AFTER_IMG = "https://customer-assets.emergentagent.com/job_clearout-pro/artifacts/tc3qwami_polish_20211220_193932055-Yg2q7rN9blSMeXPM.avif";

const trustPoints = [
  { icon: Star, text: '5-Star Google Rating' },
  { icon: Users, text: '85+ Reviews' },
  { icon: Shield, text: 'Fully Insured' },
  { icon: Leaf, text: 'Eco-Friendly Disposal' },
  { icon: MapPin, text: 'Locally Owned' },
];

const services = [
  { icon: Sofa, name: 'Furniture Removal' },
  { icon: Monitor, name: 'Appliance Removal' },
  { icon: BedDouble, name: 'Mattress Removal' },
  { icon: Home, name: 'Home Cleanouts' },
  { icon: Warehouse, name: 'Barn Cleanouts' },
  { icon: Car, name: 'Garage Cleanouts' },
  { icon: Home, name: 'Attic & Basement Cleanouts' },
  { icon: Building2, name: 'Foreclosure Cleanouts' },
  { icon: Trash2, name: 'Debris Removal' },
  { icon: TreePine, name: 'Yard Cleanup' },
  { icon: Briefcase, name: 'Office Cleanouts' },
  { icon: Hammer, name: 'Interior & Exterior Demolition' },
  { icon: Fence, name: 'Deck Removal' },
  { icon: Bath, name: 'Hot Tub Removal' },
  { icon: Truck, name: 'Dump Trailer Rental' },
  { icon: ThumbsUp, name: 'And Much More' },
];

const howItWorks = [
  { step: '01', title: 'Request a Quote', description: 'Call, text, or fill out our form for a free estimate.' },
  { step: '02', title: 'Get Your Quote', description: "We review your job and give you an upfront, fair price." },
  { step: '03', title: 'We Haul It Away', description: 'We show up, load it up, and haul it away. Done!' },
];

const whyChooseUs = [
  { icon: Clock, title: 'Fast Response', description: 'We get back to you quickly and show up on time.' },
  { icon: Users, title: 'Professional Service', description: 'Friendly, hardworking team that treats your property with respect.' },
  { icon: DollarSign, title: 'Fair Pricing', description: 'Honest, upfront quotes with no hidden fees or surprises.' },
  { icon: Shield, title: 'Fully Insured', description: 'General liability and commercial auto coverage for your peace of mind.' },
  { icon: Leaf, title: 'Eco-Friendly', description: 'We recycle, reuse, and donate as much as possible.' },
  { icon: Building2, title: 'Residential & Commercial', description: 'We handle jobs of all sizes for homes and businesses.' },
];

const reviews = [
  { name: 'Jehan Day', text: 'They were the BEST to work with! Mario was extremely professional and the communication was outstanding! Would work with them again!' },
  { name: 'Hommy', text: 'We were very happy with our clean up process and would highly recommend. Punctual, creative and beyond hard working.' },
  { name: 'Travis Olson', text: 'Awesome response time and did the job fast! Kept in touch with me through the whole process. Super friendly too!' },
  { name: 'Nicole Medak', text: 'From start to finish the job was handled professionally. Estimate was accurate and there were no surprise additional costs.' },
  { name: 'James M', text: 'Responsive and friendly staff with great communication. They were respectful of my property and completed the job quickly.' },
  { name: 'Thomas Payne', text: 'Very fast, very professional, and super nice and friendly guys. I will recommend your services to anyone else I know.' },
];

const serviceAreas = [
  'Cherokee County, OK',
  'Adair County, OK',
  'Mayes County, OK',
  'Delaware County, OK',
  'Craig County, OK',
  'Ottawa County, OK',
  'Wagoner County, OK',
  'Muskogee County, OK',
  'Sequoyah County, OK',
];

const faqs = [
  {
    question: 'Do you service emergency or urgent jobs?',
    answer: 'We ask for as much notice as possible, but same day or immediate service may be available depending on scheduling. Call or text us to discuss your needs.'
  },
  {
    question: 'How much do you charge?',
    answer: 'Pricing is based on volume, meaning the amount of junk being removed. For an accurate free estimate, customers can text photos to 918-321-0654 or email support@junkremovalmd.com with their name and service address.'
  },
  {
    question: 'What areas do you service?',
    answer: 'We service most of Northeast Oklahoma, including Cherokee County and surrounding counties. If you are unsure, call or text and we will let you know.'
  },
  {
    question: 'What items do you accept?',
    answer: 'We accept most unwanted items including furniture, appliances, mattresses, rugs, dressers, yard waste, construction debris, wood, metal, and more.'
  },
  {
    question: 'What items do you not accept?',
    answer: 'We currently do not accept chemicals including automotive fluids, pesticides, herbicides, insecticides, acids, large amounts of concrete, dirt, or animal carcasses.'
  },
  {
    question: 'Are you insured?',
    answer: 'Yes. Junk Removal M.D. carries both general liability and commercial auto insurance.'
  },
  {
    question: 'Do I need to be present for service?',
    answer: 'No. Customers do not have to be present as long as the junk is accessible and payment has been arranged ahead of time.'
  },
  {
    question: 'What payment options do you accept?',
    answer: 'We accept all forms of payment.'
  },
];

export default function HomePage() {
  return (
    <div data-testid="home-page">
      {/* Hero Section with Brand Background */}
      <section className="relative min-h-[650px] md:min-h-[750px] flex items-center" data-testid="hero-section">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${HERO_BG})` }}
        />
        {/* Dark overlay for text readability */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#1a1a1a]/95 via-[#1a1a1a]/85 to-[#1a1a1a]/70" />
        
        <div className="relative max-w-7xl mx-auto px-4 md:px-12 py-20 w-full">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="font-['Barlow_Condensed'] text-5xl md:text-7xl font-bold text-white leading-[0.9] mb-6 animate-fade-in-up">
                The Best Junk Removal Service in Northeast Oklahoma
              </h1>
              <p className="text-xl md:text-2xl text-gray-300 mb-4 animate-fade-in-up stagger-1">
                Fast, insured, eco-friendly junk removal for homes, businesses, cleanouts, debris removal, and more.
              </p>
              <p className="text-lg text-gray-400 mb-8 animate-fade-in-up stagger-2">
                What's your time worth? Call today for reliable service you can trust. <span className="text-[#dc2626] font-semibold">Sick of junk? We can help.</span>
              </p>
              <div className="flex flex-col sm:flex-row gap-4 animate-fade-in-up stagger-3">
                <a href="tel:9183210654">
                  <Button 
                    className="bg-[#dc2626] hover:bg-[#b91c1c] text-white font-bold uppercase tracking-wider py-6 px-8 text-lg btn-press w-full sm:w-auto shadow-lg shadow-red-900/30"
                    data-testid="hero-call-btn"
                  >
                    <Phone className="w-5 h-5 mr-2" />
                    Call Today (918) 321-0654
                  </Button>
                </a>
                <Link to="/contact">
                  <Button 
                    variant="outline"
                    className="bg-white/10 hover:bg-white/20 text-white border-white/30 font-bold uppercase tracking-wider py-6 px-8 text-lg btn-press w-full sm:w-auto"
                    data-testid="hero-estimate-btn"
                  >
                    Request a Free Estimate
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Bar */}
      <section className="bg-white border-b py-6" data-testid="trust-bar">
        <div className="max-w-7xl mx-auto px-4 md:px-12">
          <div className="flex flex-wrap justify-center gap-6 md:gap-12">
            {trustPoints.map((point, index) => (
              <div key={index} className="flex items-center gap-2 text-[#1a1a1a]">
                <point.icon className="w-5 h-5 text-[#dc2626]" />
                <span className="text-sm font-semibold">{point.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Preview with Before/After */}
      <section className="py-20 md:py-32 bg-white" data-testid="about-preview">
        <div className="max-w-7xl mx-auto px-4 md:px-12">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-['Barlow_Condensed'] text-4xl md:text-5xl font-bold text-[#1a1a1a] mb-6">
                Your Satisfaction Is Our Priority
              </h2>
              <p className="text-gray-600 text-lg mb-8 leading-relaxed">
                At Junk Removal M.D., we provide a fully insured, eco-friendly junk removal service to Northeast Oklahoma, ensuring safe and efficient handling of unwanted items for both residential and commercial customers. We recycle, reuse, and donate as much as possible while safely and properly disposing of the excess.
              </p>
              <ul className="space-y-4">
                {[
                  'Locally owned, operated, and based in Tahlequah, Oklahoma',
                  'Committed to customer satisfaction',
                  'Residential and commercial service',
                  'Safe, efficient, and professional removal',
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-[#dc2626] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <ChevronRight className="w-4 h-4 text-white" />
                    </div>
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
              <Link to="/about" className="inline-block mt-8">
                <Button variant="ghost" className="text-[#dc2626] hover:text-[#b91c1c] font-semibold p-0" data-testid="about-link">
                  Learn More About Us <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            </div>
            
            {/* Before/After Transformation */}
            <div className="relative">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <div className="aspect-[4/3] rounded-xl overflow-hidden shadow-lg">
                    <img 
                      src={BEFORE_IMG}
                      alt="Before - Cluttered space needing cleanup"
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </div>
                  <p className="text-center font-bold text-[#1a1a1a] uppercase text-sm tracking-wide">Before</p>
                </div>
                <div className="space-y-2">
                  <div className="aspect-[4/3] rounded-xl overflow-hidden shadow-lg">
                    <img 
                      src={AFTER_IMG}
                      alt="After - Clean and cleared space"
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </div>
                  <p className="text-center font-bold text-[#dc2626] uppercase text-sm tracking-wide">After</p>
                </div>
              </div>
              <div className="mt-6 text-center">
                <span className="inline-block bg-[#1a1a1a] text-white px-6 py-3 rounded-full shadow-lg font-['Barlow_Condensed'] text-lg font-bold">
                  The Transformation
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Preview */}
      <section className="py-20 md:py-32 bg-[#f5f5f5]" data-testid="services-preview">
        <div className="max-w-7xl mx-auto px-4 md:px-12">
          <div className="text-center mb-12">
            <h2 className="font-['Barlow_Condensed'] text-4xl md:text-5xl font-bold text-[#1a1a1a] mb-4">
              Our Services
            </h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              From single items to full property cleanouts, we handle it all with professionalism and care.
            </p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {services.map((service, index) => (
              <div 
                key={index}
                className="service-card bg-white p-6 rounded-xl hover:shadow-md transition-all cursor-pointer group"
                data-testid={`service-card-${index}`}
              >
                <service.icon className="w-8 h-8 text-[#dc2626] mb-3 group-hover:scale-110 transition-transform" />
                <h3 className="font-semibold text-[#1a1a1a] text-sm">{service.name}</h3>
              </div>
            ))}
          </div>
          
          <div className="text-center mt-12">
            <Link to="/services">
              <Button className="bg-[#1a1a1a] hover:bg-[#333] text-white font-bold uppercase tracking-wider px-8 py-6 btn-press" data-testid="view-all-services-btn">
                View All Services
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 md:py-32 bg-[#1a1a1a]" data-testid="how-it-works">
        <div className="max-w-7xl mx-auto px-4 md:px-12">
          <div className="text-center mb-16">
            <h2 className="font-['Barlow_Condensed'] text-4xl md:text-5xl font-bold text-white mb-4">
              How It Works
            </h2>
            <p className="text-gray-400 text-lg">
              Getting rid of junk has never been easier.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {howItWorks.map((item, index) => (
              <div key={index} className="text-center">
                <div className="inline-flex items-center justify-center w-20 h-20 bg-[#dc2626] rounded-full mb-6">
                  <span className="font-['Barlow_Condensed'] text-3xl font-bold text-white">{item.step}</span>
                </div>
                <h3 className="font-['Barlow_Condensed'] text-2xl font-bold text-white mb-3">{item.title}</h3>
                <p className="text-gray-400">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 md:py-32 bg-white" data-testid="why-choose-us">
        <div className="max-w-7xl mx-auto px-4 md:px-12">
          <div className="text-center mb-12">
            <h2 className="font-['Barlow_Condensed'] text-4xl md:text-5xl font-bold text-[#1a1a1a] mb-4">
              Why Choose Junk Removal M.D.?
            </h2>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {whyChooseUs.map((item, index) => (
              <div key={index} className="flex gap-4 p-6 bg-[#f5f5f5] rounded-xl">
                <div className="w-12 h-12 bg-[#dc2626] rounded-lg flex items-center justify-center flex-shrink-0">
                  <item.icon className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-['Barlow_Condensed'] text-xl font-bold text-[#1a1a1a] mb-2">{item.title}</h3>
                  <p className="text-gray-600 text-sm">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Reviews */}
      <section className="py-20 md:py-32 bg-[#f5f5f5]" data-testid="featured-reviews">
        <div className="max-w-7xl mx-auto px-4 md:px-12">
          <div className="text-center mb-12">
            <div className="flex items-center justify-center gap-1 mb-4">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-6 h-6 fill-yellow-400 text-yellow-400" />
              ))}
            </div>
            <h2 className="font-['Barlow_Condensed'] text-4xl md:text-5xl font-bold text-[#1a1a1a] mb-2">
              What Our Customers Say
            </h2>
            <p className="text-gray-600">5 Stars on Google · 85 Reviews</p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {reviews.map((review, index) => (
              <div 
                key={index}
                className="review-card bg-white p-6 rounded-2xl relative"
                data-testid={`review-card-${index}`}
              >
                <div className="review-quote relative z-10">
                  <div className="flex gap-1 mb-3">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>
                  <p className="text-gray-700 mb-4 text-sm leading-relaxed">"{review.text}"</p>
                  <p className="font-semibold text-[#1a1a1a]">{review.name}</p>
                </div>
              </div>
            ))}
          </div>
          
          <div className="text-center mt-12">
            <Link to="/reviews">
              <Button variant="outline" className="border-[#1a1a1a] text-[#1a1a1a] hover:bg-[#1a1a1a] hover:text-white font-bold uppercase tracking-wider px-8 py-6 btn-press" data-testid="view-all-reviews-btn">
                Read All Reviews
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Service Area - Text Only */}
      <section className="py-20 md:py-32 bg-[#1a1a1a]" data-testid="service-area">
        <div className="max-w-7xl mx-auto px-4 md:px-12">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="font-['Barlow_Condensed'] text-4xl md:text-5xl font-bold text-white mb-6">
              Proudly Serving Northeast Oklahoma
            </h2>
            <p className="text-gray-400 mb-10">
              Based in Tahlequah, we serve Cherokee County and the surrounding areas of Northeast Oklahoma.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-10">
              {serviceAreas.map((area, index) => (
                <div key={index} className="flex items-center justify-center gap-2 bg-white/5 px-4 py-3 rounded-lg">
                  <MapPin className="w-4 h-4 text-[#dc2626]" />
                  <span className="text-gray-300 text-sm">{area}</span>
                </div>
              ))}
            </div>
            <p className="text-gray-500 text-sm mb-8">
              If your location is not listed, don't worry. We still may be able to help.
            </p>
            <a href="tel:9183210654">
              <Button className="bg-[#dc2626] hover:bg-[#b91c1c] text-white font-bold uppercase tracking-wider px-8 py-4 btn-press" data-testid="service-area-call-btn">
                <Phone className="w-5 h-5 mr-2" />
                Call to Confirm Service
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 md:py-32 bg-white" data-testid="faq-section">
        <div className="max-w-3xl mx-auto px-4 md:px-12">
          <div className="text-center mb-12">
            <h2 className="font-['Barlow_Condensed'] text-4xl md:text-5xl font-bold text-[#1a1a1a] mb-4">
              Frequently Asked Questions
            </h2>
          </div>
          
          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, index) => (
              <AccordionItem 
                key={index} 
                value={`faq-${index}`}
                className="bg-[#f5f5f5] rounded-xl border-none"
              >
                <AccordionTrigger className="px-6 py-4 hover:no-underline faq-trigger text-left" data-testid={`faq-trigger-${index}`}>
                  <span className="font-semibold text-[#1a1a1a]">{faq.question}</span>
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-4 faq-content">
                  <p className="text-gray-600">{faq.answer}</p>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 md:py-32 bg-[#dc2626]" data-testid="final-cta">
        <div className="max-w-4xl mx-auto px-4 md:px-12 text-center">
          <h2 className="font-['Barlow_Condensed'] text-4xl md:text-6xl font-bold text-white mb-6">
            Sick of Junk? We Can Help.
          </h2>
          <p className="text-white/90 text-xl mb-10">
            Call today or request a free estimate and we'll get back to you as quickly as possible.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="tel:9183210654">
              <Button className="bg-white text-[#dc2626] hover:bg-gray-100 font-bold uppercase tracking-wider py-6 px-8 text-lg btn-press" data-testid="final-call-btn">
                <Phone className="w-5 h-5 mr-2" />
                Call Today
              </Button>
            </a>
            <Link to="/contact">
              <Button variant="outline" className="bg-transparent border-2 border-white text-white hover:bg-white/10 font-bold uppercase tracking-wider py-6 px-8 text-lg btn-press" data-testid="final-estimate-btn">
                Request a Free Estimate
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
